import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FileText, TrendingUp, AlertCircle } from 'lucide-react';
import Statistics from '../components/Statistics';
import './Dashboard.css';

const Dashboard = () => {
  const [denuncias, setDenuncias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'denuncias'), orderBy('created_at', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const denunciasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDenuncias(denunciasData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading denuncias:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredDenuncias = denuncias.filter(denuncia => {
    if (filter === 'all') return true;
    return denuncia.status === filter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      'Nueva': 'status-badge status-nueva',
      'En Revision': 'status-badge status-revision',
      'Cerrada': 'status-badge status-cerrada'
    };
    return badges[status] || 'status-badge';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate();
      return format(date, "dd 'de' MMMM, yyyy HH:mm", { locale: es });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Panel de Administración</h1>
          <p>Gestión de denuncias y estadísticas</p>
        </div>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 130, 32, 0.1)' }}>
            <FileText size={24} color="#F58220" />
          </div>
          <div className="stat-content">
            <h3>{denuncias.length}</h3>
            <p>Total Denuncias</p>
          </div>
        </div>

        <div className="stat-card card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>
            <AlertCircle size={24} color="#DC2626" />
          </div>
          <div className="stat-content">
            <h3>{denuncias.filter(d => d.status === 'Nueva').length}</h3>
            <p>Nuevas</p>
          </div>
        </div>

        <div className="stat-card card">
          <div className="stat-icon" style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
            <TrendingUp size={24} color="#16A34A" />
          </div>
          <div className="stat-content">
            <h3>{denuncias.filter(d => d.status === 'En Revision').length}</h3>
            <p>En Revisión</p>
          </div>
        </div>
      </div>

      <Statistics denuncias={denuncias} />

      <div className="denuncias-section card">
        <div className="section-header">
          <h2>Bandeja de Denuncias</h2>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Todas ({denuncias.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'Nueva' ? 'active' : ''}`}
              onClick={() => setFilter('Nueva')}
            >
              Nuevas ({denuncias.filter(d => d.status === 'Nueva').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'En Revision' ? 'active' : ''}`}
              onClick={() => setFilter('En Revision')}
            >
              En Revisión ({denuncias.filter(d => d.status === 'En Revision').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'Cerrada' ? 'active' : ''}`}
              onClick={() => setFilter('Cerrada')}
            >
              Cerradas ({denuncias.filter(d => d.status === 'Cerrada').length})
            </button>
          </div>
        </div>

        {filteredDenuncias.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} color="#999" />
            <p>No hay denuncias para mostrar</p>
          </div>
        ) : (
          <div className="denuncias-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Tipo de Delito</th>
                  <th>Estado</th>
                  <th>Anónima</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredDenuncias.map(denuncia => (
                  <tr key={denuncia.id}>
                    <td className="id-cell">{denuncia.id.substring(0, 8)}</td>
                    <td>{formatDate(denuncia.created_at)}</td>
                    <td>
                      <div className="delitos-cell">
                        {denuncia.delitos.slice(0, 2).join(', ')}
                        {denuncia.delitos.length > 2 && ` +${denuncia.delitos.length - 2}`}
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadge(denuncia.status)}>
                        {denuncia.status}
                      </span>
                    </td>
                    <td>
                      {denuncia.denunciante?.es_anonimo ? (
                        <span className="anonimo-badge">Sí</span>
                      ) : (
                        <span className="identificado-badge">No</span>
                      )}
                    </td>
                    <td>
                      <button 
                        className="btn-view"
                        onClick={() => navigate(`/admin/denuncia/${denuncia.id}`)}
                      >
                        Ver Detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
